import { ALL_AI_CRAWLERS } from '../shared';
import type {
  CheckResult,
  DiagnosticsReportResponse,
  RobotsTxtCheckResultData,
  RobotsTxtUnverifiedReason,
  UserAgentResult,
} from './api/diagnosticsReportApi';

const CHECK_DISPLAY_NAMES: Record<string, string> = {
  Integration: 'Prerender Integration',
  CDN: 'CDN Configuration',
  WAF: 'Web Application Firewall (WAF)',
  'robots.txt': 'Robots.txt Rules',
};

const CHECK_ORDER = ['Integration', 'robots.txt', 'CDN', 'WAF'];

// Unreachable until the ai-insights-amazonbot flag retires and Gemini leaves
// ALL_AI_CRAWLERS; stored reports still name Google-Extended, so keep this then.
const RETIRED_AGENT_PROVIDERS: ReadonlyMap<string, string> = new Map([['Google-Extended', 'Gemini']]);

export function mapUserAgentToProvider(agentName: string): string {
  for (const crawler of ALL_AI_CRAWLERS) {
    if (crawler.bots.some((bot) => bot.name === agentName)) {
      return crawler.provider;
    }
  }
  return RETIRED_AGENT_PROVIDERS.get(agentName) ?? agentName;
}

// A check that reached the site but could not establish an answer. `result` is absent
// on the wire (JSON drops the undefined), so the reason is what identifies it.
function isCheckUnverified(checkResult: CheckResult): boolean {
  return (
    typeof checkResult === 'object' &&
    checkResult !== null &&
    (checkResult as RobotsTxtCheckResultData).unverifiedReason !== undefined
  );
}

function isCheckErrored(checkResult: CheckResult): boolean {
  if (typeof checkResult !== 'object' || checkResult === null) return false;
  return checkResult.result === 'error' || isCheckUnverified(checkResult);
}

function isCheckBlocked(checkResult: CheckResult): boolean {
  if (typeof checkResult === 'boolean') return !checkResult;
  if (checkResult === null) return false;
  // Tested after the unverified case so a check we could not read is never counted as
  // a confirmed block.
  if (isCheckUnverified(checkResult)) return false;
  return checkResult.result === false;
}

function isCheckMissing(checkResult: CheckResult | undefined): checkResult is undefined {
  return checkResult === undefined;
}

export type CheckRow = {
  checkKey: string;
  displayName: string;
  blockedProviders: string[];
  failed: boolean;
  rawResults: Record<string, CheckResult>;
};

function checkRowPriority(row: CheckRow): number {
  if (row.blockedProviders.length > 0) return 0;
  if (row.failed) return 1;
  return 2;
}

export function getCheckRows(userAgents: DiagnosticsReportResponse['userAgents']): CheckRow[] {
  const agentNames = Object.keys(userAgents);

  const rows = CHECK_ORDER.map((checkKey) => {
    const blocked = new Set<string>();
    const rawResults: Record<string, CheckResult> = {};
    let allMissing = true;
    let hasErrored = false;

    for (const agentName of agentNames) {
      const checks = userAgents[agentName];
      const checkResult = checks[checkKey];

      if (!isCheckMissing(checkResult)) {
        allMissing = false;
        rawResults[agentName] = checkResult;
        if (isCheckErrored(checkResult)) {
          hasErrored = true;
        } else if (isCheckBlocked(checkResult)) {
          blocked.add(mapUserAgentToProvider(agentName));
        }
      }
    }

    // A check is "failed to run" when no crawler returned a result at all, OR when
    // at least one crawler's request errored. In the latter case the check's
    // result is incomplete and untrustworthy, so we suppress any blocked-crawler
    // tags for the row and surface the failure instead.
    const failed = (agentNames.length > 0 && allMissing) || hasErrored;

    return {
      checkKey,
      displayName: CHECK_DISPLAY_NAMES[checkKey] ?? checkKey,
      blockedProviders: failed ? [] : Array.from(blocked),
      failed,
      rawResults,
    };
  });

  return rows.sort((a, b) => checkRowPriority(a) - checkRowPriority(b));
}

// The blocks the report can actually stand behind — derived from the check rows,
// which already drop blocks for any check that couldn't finish (errored / all-missing),
// since a check we couldn't complete isn't trustworthy. Prefer this over
// getBlockedProviders for anything user-facing (summary tag + all-clear note) so we
// never surface a block from an untrusted check. (PRE-3158 / PRE-3049)
export function getBlockedProvidersFromRows(checkRows: CheckRow[]): string[] {
  const blocked = new Set<string>();

  for (const row of checkRows) {
    for (const provider of row.blockedProviders) {
      blocked.add(provider);
    }
  }

  return Array.from(blocked);
}

export function getBlockedProviders(userAgents: DiagnosticsReportResponse['userAgents']): string[] {
  const blocked = new Set<string>();

  for (const [agentName, checks] of Object.entries(userAgents)) {
    const hasBlockedCheck = Object.values(checks).some(
      (result) => !isCheckMissing(result) && isCheckBlocked(result),
    );
    if (hasBlockedCheck) {
      blocked.add(mapUserAgentToProvider(agentName));
    }
  }

  return Array.from(blocked);
}

export function getCrawlersTested(userAgents: DiagnosticsReportResponse['userAgents']): string[] {
  const providers = new Set<string>();

  for (const agentName of Object.keys(userAgents)) {
    providers.add(mapUserAgentToProvider(agentName));
  }

  return Array.from(providers);
}

// The cause to act on, taken from the first failure that names one. Where crawlers name
// different causes, acting on one of them still gives the customer somewhere to start;
// withholding guidance because the failures disagree would leave a row that says the
// check could not run and offers no way forward.
export function getFailureReason(
  rawResults: Record<string, CheckResult>,
): RobotsTxtUnverifiedReason | undefined {
  return Object.values(rawResults)
    .filter(isCheckErrored)
    .map((result) => (result as RobotsTxtCheckResultData).unverifiedReason)
    .find((reason) => reason !== undefined);
}

export function getBlockedBots(rawResults: Record<string, CheckResult>): string[] {
  return Object.entries(rawResults)
    .filter(([, result]) => isCheckBlocked(result))
    .map(([botName]) => botName);
}

export function extractDomain(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

const FULL_RUN_PROVIDER_COUNT = 4;

export function getBlockedCrawlersDisplayText(
  userAgentResults: UserAgentResult[],
  status: 'completed' | 'partial' | 'error',
): string {
  if (status === 'error') return 'Error occurred. Rerun the report.';

  const testedProviders = new Set(userAgentResults.map((r) => mapUserAgentToProvider(r.shortName)));
  const blocked = Array.from(
    new Set(userAgentResults.filter((r) => r.result === 'failed').map((r) => mapUserAgentToProvider(r.shortName))),
  );

  if (blocked.length === 0) return 'None';
  if (blocked.length === testedProviders.size && testedProviders.size >= FULL_RUN_PROVIDER_COUNT) {
    return `All ${testedProviders.size} AI Crawlers`;
  }

  return blocked.join(', ');
}
